import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { MessageCircle, Send, Search, Loader2, ArrowLeft, Image, Paperclip, X, FileText, Download, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { chatAPI } from '../services/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Messages = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [ws, setWs] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [cameFromExternal, setCameFromExternal] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserId(user._id);
    loadConversations();
    if (user._id) {
      connectWebSocket(user._id);
    }
    return () => {
      if (ws) ws.close();
    };
  }, []);

  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId) setCameFromExternal(true);
    if (loading) return;
    if (userId && !selectedConversation) {
      const existingConv = conversations.find(
        conv => conv.otherUser?._id === userId || conv.otherUser?.id === userId
      );
      if (existingConv) {
        handleSelectConversation(existingConv);
      } else {
        createOrOpenConversation(userId);
      }
    }
  }, [searchParams, conversations, loading, selectedConversation]);

  const createOrOpenConversation = async (otherUserId) => {
    try {
      const response = await chatAPI.getOrCreateConversation(otherUserId);
      const conversation = response.data;
      if (!conversation || !conversation._id) {
        toast.error('Failed to create conversation');
        return;
      }
      setConversations(prev => {
        const exists = prev.find(c => c._id === conversation._id);
        return exists ? prev : [conversation, ...prev];
      });
      setSelectedConversation(conversation);
      setShowMobileChat(true);
      if (conversation._id) await loadMessages(conversation._id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open conversation');
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = (userId) => {
    const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/api/chat/ws/${userId}`;
    const websocket = new WebSocket(wsUrl);
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message') {
        if (selectedConversation && data.message.conversationId === selectedConversation?._id) {
          setMessages(prev => [...prev, data.message]);
        }
        loadConversations();
      }
    };
    websocket.onclose = () => {
      setTimeout(() => connectWebSocket(userId), 3000);
    };
    setWs(websocket);
  };

  const loadConversations = async () => {
    try {
      const response = await chatAPI.getConversations();
      setConversations(response.data.conversations);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await chatAPI.getMessages(conversationId);
      setMessages(response.data.messages);
      await chatAPI.markRead(conversationId);
      loadConversations();
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowMobileChat(true);
    loadMessages(conversation._id);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size must be less than 25MB');
      return;
    }
    setAttachedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachedPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachedPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachedFile(null);
    setAttachedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteConversation = (convId, e) => {
    e.stopPropagation();
    setDeleteConfirmId(convId);
  };

  const confirmDelete = async () => {
    const convId = deleteConfirmId;
    setDeleteConfirmId(null);
    try {
      await chatAPI.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c._id !== convId));
      if (selectedConversation?._id === convId) {
        setSelectedConversation(null);
        setMessages([]);
        setShowMobileChat(false);
      }
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !attachedFile) || !selectedConversation || sending) return;

    const content = messageInput;
    const file = attachedFile;
    setMessageInput('');
    clearAttachment();
    setSending(true);

    try {
      let response;
      if (file) {
        response = await chatAPI.sendMessageWithAttachment(
          selectedConversation.otherUser._id,
          content,
          file
        );
      } else {
        response = await chatAPI.sendMessage(
          selectedConversation.otherUser._id,
          content
        );
      }
      setMessages(prev => [...prev, response.data.message]);
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredConversations = conversations.filter(conv =>
    conv.otherUser?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.otherUser?.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isImageType = (type) => type && type.startsWith('image/');

  // Render attachment inside a message bubble
  const renderAttachment = (attachment, isOwn) => {
    if (!attachment || !attachment.url) return null;
    if (isImageType(attachment.fileType)) {
      return (
        <img
          src={attachment.url}
          alt={attachment.fileName || 'Image'}
          className="rounded-xl max-w-full max-h-64 object-cover cursor-pointer"
          onClick={() => window.open(attachment.url, '_blank')}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
          isOwn ? 'bg-white/10 hover:bg-white/20' : 'bg-muted hover:bg-muted/80'
        }`}
      >
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isOwn ? 'bg-white/20' : 'bg-primary/10'
        }`}>
          <FileText className={`w-4 h-4 ${isOwn ? 'text-white' : 'text-primary'}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-medium truncate ${isOwn ? 'text-white' : 'text-foreground'}`}>
            {attachment.fileName || 'File'}
          </p>
          {attachment.fileSize > 0 && (
            <p className={`text-[10px] ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
              {formatFileSize(attachment.fileSize)}
            </p>
          )}
        </div>
        <Download className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`} />
      </a>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 pt-20">
          <Card className="mt-4 overflow-hidden shadow-lg border-border" style={{ height: 'calc(100vh - 120px)' }} data-testid="messages-skeleton">
            <div className="flex h-full">
              {/* Conversations list skeleton */}
              <div className="w-full md:w-96 border-r border-border flex flex-col">
                <div className="p-4 border-b border-border">
                  <Skeleton className="h-7 w-28 mb-3" />
                  <Skeleton className="h-9 w-full rounded-lg" />
                </div>
                <div className="flex-1 px-0">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                      <Skeleton className="w-14 h-14 rounded-full flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="flex justify-between">
                          <Skeleton className="h-3.5 w-24" />
                          <Skeleton className="h-3 w-10" />
                        </div>
                        <Skeleton className="h-3 w-40" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Chat area skeleton */}
              <div className="hidden md:flex flex-1 items-center justify-center">
                <div className="text-center">
                  <Skeleton className="w-24 h-24 rounded-full mx-auto mb-4" />
                  <Skeleton className="h-5 w-36 mx-auto mb-2" />
                  <Skeleton className="h-3.5 w-52 mx-auto" />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pt-20">
        <Card className="mt-4 overflow-hidden shadow-lg border-border skeleton-fade-in" style={{ height: 'calc(100vh - 120px)' }} data-testid="messages-container">
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full md:w-96 border-r border-border flex flex-col ${
              selectedConversation && showMobileChat && window.innerWidth < 768 ? 'hidden' : ''
            }`}>
              <div className="p-4 border-b border-border">
                <h2 className="text-xl font-bold mb-3" data-testid="messages-title">Messages</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-muted/50 border-0 focus-visible:ring-1"
                    data-testid="conversations-search"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredConversations.length > 0 ? (
                  <div data-testid="conversations-list">
                    {filteredConversations.map((conversation) => (
                      <div
                        key={conversation._id}
                        className={`group flex items-center hover:bg-muted/50 transition-colors ${
                          selectedConversation?._id === conversation._id ? 'bg-primary/5' : ''
                        }`}
                      >
                        <button
                          onClick={() => handleSelectConversation(conversation)}
                          data-testid={`conversation-${conversation._id}`}
                          className="flex-1 min-w-0 px-4 py-3 flex items-center gap-3"
                        >
                          <div className="relative flex-shrink-0">
                            <Avatar className="w-14 h-14">
                              <AvatarImage src={conversation.otherUser?.profileImage} alt={conversation.otherUser?.name} />
                              <AvatarFallback className="bg-primary/10 text-primary font-semibold">{conversation.otherUser?.name?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            {conversation.unreadCount > 0 && (
                              <Badge className="absolute -top-1 -right-1 min-w-[20px] h-5 text-xs bg-primary hover:bg-primary text-white border-2 border-card">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="flex items-center justify-between mb-0.5">
                              <p className={`text-sm truncate ${conversation.unreadCount > 0 ? 'font-bold text-foreground' : 'font-semibold text-foreground'}`}>
                                {conversation.otherUser?.name || 'Unknown User'}
                              </p>
                              <span className="text-[11px] text-muted-foreground flex-shrink-0 ml-2 group-hover:hidden">
                                {conversation.lastMessage ? formatTime(conversation.lastMessage.createdAt) : ''}
                              </span>
                            </div>
                            <p className={`text-[13px] truncate ${
                              conversation.unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'
                            }`}>
                              {conversation.lastMessage?.content || 'Start a conversation'}
                            </p>
                          </div>
                        </button>
                        {/* Delete button - separate from conversation button */}
                        <button
                          onClick={(e) => handleDeleteConversation(conversation._id, e)}
                          data-testid={`delete-conversation-${conversation._id}`}
                          className="flex-shrink-0 mr-3 p-2 rounded-full transition-all opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title="Delete conversation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full px-6 py-12" data-testid="empty-conversations">
                    <div className="w-20 h-20 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center mb-4">
                      <Send className="w-8 h-8 text-muted-foreground/40 -rotate-45" />
                    </div>
                    <p className="text-lg font-semibold text-foreground mb-1">Your Messages</p>
                    <p className="text-sm text-muted-foreground text-center">
                      Send a message to start a conversation.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Area */}
            {selectedConversation ? (
              <div className={`flex-1 flex flex-col ${
                showMobileChat && window.innerWidth < 768 ? 'w-full' : ''
              }`} data-testid="chat-area">
                {/* Chat Header */}
                <div className="px-4 py-3 border-b border-border flex items-center gap-3 bg-card">
                  <button
                    onClick={() => {
                      if (cameFromExternal) {
                        navigate(-1);
                      } else {
                        setSelectedConversation(null);
                        setShowMobileChat(false);
                      }
                    }}
                    className="md:hidden text-foreground"
                    data-testid="chat-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div 
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity flex-1"
                    onClick={() => {
                      if (selectedConversation.otherUser?.username) {
                        navigate(`/profile/${selectedConversation.otherUser.username}`);
                      }
                    }}
                    data-testid="chat-header-profile"
                  >
                    <Avatar className="w-10 h-10 ring-2 ring-primary/20">
                      <AvatarImage src={selectedConversation.otherUser?.profileImage} alt={selectedConversation.otherUser?.name} />
                      <AvatarFallback className="text-sm bg-primary/10 text-primary">{selectedConversation.otherUser?.name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-sm text-foreground leading-tight">{selectedConversation.otherUser?.name || 'Unknown User'}</p>
                      <p className="text-[11px] text-muted-foreground">@{selectedConversation.otherUser?.username || 'unknown'}</p>
                    </div>
                  </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="messages-area">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Avatar className="w-20 h-20 mb-3">
                        <AvatarImage src={selectedConversation.otherUser?.profileImage} />
                        <AvatarFallback className="text-2xl bg-primary/10 text-primary">{selectedConversation.otherUser?.name?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                      <p className="font-semibold text-foreground">{selectedConversation.otherUser?.name}</p>
                      <p className="text-sm text-muted-foreground mb-4">@{selectedConversation.otherUser?.username}</p>
                      <p className="text-xs text-muted-foreground">Send a message to start chatting</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {messages.map((message, index) => {
                        const isOwn = message.sender._id === currentUserId;
                        const showAvatar = !isOwn && (index === 0 || messages[index - 1]?.sender._id !== message.sender._id);
                        const isLastInGroup = index === messages.length - 1 || messages[index + 1]?.sender._id !== message.sender._id;
                        
                        return (
                          <div
                            key={message._id}
                            className={`flex items-end gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${message._id}`}
                          >
                            {!isOwn && (
                              <div className="w-7 flex-shrink-0">
                                {showAvatar && (
                                  <Avatar className="w-7 h-7">
                                    <AvatarImage src={selectedConversation.otherUser?.profileImage} />
                                    <AvatarFallback className="text-[10px]">{selectedConversation.otherUser?.name?.[0]}</AvatarFallback>
                                  </Avatar>
                                )}
                              </div>
                            )}
                            <div className={`max-w-[65%] ${isOwn ? 'items-end' : 'items-start'}`}>
                              <div
                                className={`rounded-2xl overflow-hidden ${
                                  isOwn
                                    ? `bg-primary text-primary-foreground ${isLastInGroup ? 'rounded-br-md' : ''}`
                                    : `bg-secondary text-foreground ${isLastInGroup ? 'rounded-bl-md' : ''}`
                                }`}
                              >
                                {message.attachment && renderAttachment(message.attachment, isOwn)}
                                {message.content && (
                                  <p className={`text-[13px] leading-relaxed px-3 ${message.attachment ? 'pt-1 pb-2' : 'py-2'}`}>
                                    {message.content}
                                  </p>
                                )}
                              </div>
                              {isLastInGroup && (
                                <p className={`text-[10px] text-muted-foreground mt-0.5 ${isOwn ? 'text-right' : 'text-left'} px-1`}>
                                  {formatTime(message.createdAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Attachment Preview */}
                {attachedFile && (
                  <div className="px-4 py-2 border-t border-border bg-muted/30" data-testid="attachment-preview">
                    <div className="flex items-center gap-3">
                      {attachedPreview ? (
                        <img src={attachedPreview} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{attachedFile.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(attachedFile.size)}</p>
                      </div>
                      <button 
                        onClick={clearAttachment} 
                        className="p-1 hover:bg-muted rounded-full transition-colors"
                        data-testid="clear-attachment-btn"
                      >
                        <X className="w-5 h-5 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="px-4 py-3 border-t border-border bg-card" data-testid="message-input-area">
                  <div className="flex items-center gap-2">
                    {/* Photo attach button */}
                    <button
                      onClick={() => {
                        fileInputRef.current.accept = 'image/*';
                        fileInputRef.current.click();
                      }}
                      className="p-2 rounded-full hover:bg-muted transition-colors text-primary"
                      data-testid="attach-photo-btn"
                      title="Attach photo"
                    >
                      <Image className="w-5 h-5" />
                    </button>
                    {/* File attach button */}
                    <button
                      onClick={() => {
                        fileInputRef.current.accept = '*/*';
                        fileInputRef.current.click();
                      }}
                      className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground"
                      data-testid="attach-file-btn"
                      title="Attach file"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      data-testid="file-input"
                    />
                    <Input
                      type="text"
                      placeholder="Message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      className="flex-1 bg-muted/50 border-0 rounded-full focus-visible:ring-1"
                      disabled={sending}
                      data-testid="message-text-input"
                    />
                    <Button
                      onClick={handleSendMessage}
                      size="icon"
                      variant="ghost"
                      className={`rounded-full transition-colors ${
                        messageInput.trim() || attachedFile ? 'text-primary hover:text-primary hover:bg-primary/10' : 'text-muted-foreground'
                      }`}
                      disabled={(!messageInput.trim() && !attachedFile) || sending}
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="hidden md:flex flex-1 items-center justify-center" data-testid="no-chat-selected">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-10 h-10 text-muted-foreground/40 -rotate-45" />
                  </div>
                  <p className="text-xl font-semibold text-foreground mb-1">Your Messages</p>
                  <p className="text-sm text-muted-foreground">Send a message to start a conversation.</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Delete Confirmation Dialog */}
        {deleteConfirmId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="delete-confirm-overlay" onClick={() => setDeleteConfirmId(null)}>
            <div className="bg-card rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl border border-border" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-foreground text-center mb-2">Delete Conversation</h3>
              <p className="text-sm text-muted-foreground text-center mb-6">Do you want to delete this conversation? This action cannot be undone.</p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDeleteConfirmId(null)}
                  data-testid="delete-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={confirmDelete}
                  data-testid="delete-confirm-btn"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
